
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Play, Video, Youtube } from "lucide-react";

interface Video {
  id: string;
  title: string;
  channel: string;
  views: string;
}

interface CareerGuide {
  title: string;
  content: string[];
}

interface CareerGuideDialogProps {
  career: {
    id: string;
    title: string;
    description: string;
    skills: string[];
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// This would ideally come from an API or database
const getCareerVideos = (careerId: string): Video[] => {
  const videosByCareer: Record<string, Video[]> = {
    "software-engineer": [
      {
        id: "Dv7gLpW91RU",
        title: "What do Software Engineers Actually Do?",
        channel: "Mayuko",
        views: "1.2M"
      },
      {
        id: "RvYYCGs8L0M",
        title: "How to Become a Software Developer in 2023",
        channel: "Traversy Media",
        views: "890K"
      },
      {
        id: "2yvZhih_jLg",
        title: "A Day in the Life of a Software Engineer (Work From Home)",
        channel: "Kenny Gunderman",
        views: "710K"
      }
    ],
    "data-scientist": [
      {
        id: "X6orAXDIrds",
        title: "What is Data Science? | Explained for Beginners",
        channel: "365 Data Science",
        views: "1.5M"
      },
      {
        id: "xC-c7E5PK0Y",
        title: "What Do Data Scientists Actually Do?",
        channel: "Joma Tech",
        views: "2.1M"
      },
      {
        id: "N6BghzuFLIg",
        title: "A Day In The Life Of A Data Scientist",
        channel: "DataLab",
        views: "430K"
      }
    ],
    "ux-designer": [
      {
        id: "5CxXhyhY5hI", 
        title: "What is UX Design? | Explained for Beginners",
        channel: "Google for Developers",
        views: "1.1M"
      },
      {
        id: "ziQMf6NUpUg",
        title: "A Day in the Life of a UX Designer",
        channel: "AJ&Smart",
        views: "980K"
      },
      {
        id: "fHsa1kBmPfU",
        title: "Getting Started in UX Design in 2023",
        channel: "Mizko",
        views: "420K"
      }
    ],
    "environmental-scientist": [
      {
        id: "Dr4jhooevHE",
        title: "What Environmental Scientists Do",
        channel: "Science Careers",
        views: "380K"
      },
      {
        id: "PmoriLhZ8HI",
        title: "A Day in the Life of an Environmental Scientist",
        channel: "EnvironmentalScience.org",
        views: "210K"
      },
      {
        id: "KYibrekr__8",
        title: "How to Become an Environmental Scientist",
        channel: "DragonFly Education",
        views: "150K"
      }
    ],
    "marketing-specialist": [
      {
        id: "POseoJYtGKE",
        title: "What is Digital Marketing? | Explained for Beginners",
        channel: "Simplilearn",
        views: "1.8M"
      },
      {
        id: "4RQh-vFQ0MQ",
        title: "A Day in the Life of a Digital Marketer",
        channel: "Vanessa Lau",
        views: "570K"
      },
      {
        id: "JJ8ScJ-zYHQ",
        title: "5 Digital Marketing Skills to Master",
        channel: "Neil Patel",
        views: "620K"
      }
    ],
    "healthcare-administrator": [
      {
        id: "nFoF3bIAFaE",
        title: "What is Healthcare Administration?",
        channel: "Healthcare Careers",
        views: "290K"
      },
      {
        id: "2Wy-XAB-l6I",
        title: "Day in the Life: Healthcare Administrator",
        channel: "American College of Healthcare Executives",
        views: "180K"
      },
      {
        id: "ZFIkYe1_lOU",
        title: "Healthcare Administration Career Overview",
        channel: "CareerVidz",
        views: "210K"
      }
    ]
  };
  
  return videosByCareer[careerId] || [];
};

// This would also ideally come from an API or database
const getCareerGuides = (careerId: string): CareerGuide[] => {
  const guidesByCareer: Record<string, CareerGuide[]> = {
    "software-engineer": [
      {
        title: "Getting Started (For Teens)",
        content: [
          "Learn basic programming with free platforms like Scratch or Code.org",
          "Build simple websites using HTML, CSS, and JavaScript",
          "Join a coding club at school or online",
          "Create small games or apps that solve everyday problems"
        ]
      },
      {
        title: "Learning Path",
        content: [
          "Start with fun courses on Khan Academy or freeCodeCamp",
          "Take Computer Science classes at your school if available",
          "Join coding bootcamps designed for teens during summer breaks",
          "Work on personal projects you're passionate about"
        ]
      },
      {
        title: "Next Steps",
        content: [
          "Find a mentor who works in tech",
          "Look for internships or job shadowing opportunities",
          "Consider Computer Science or related fields for college",
          "Build a simple portfolio website to showcase your projects"
        ]
      }
    ],
    "data-scientist": [
      {
        title: "Skills to Start Building Now",
        content: [
          "Get comfortable with math (especially statistics and algebra)",
          "Learn a programming language like Python",
          "Practice analyzing data from everyday things (sports stats, video game data)",
          "Join science or math clubs at school"
        ]
      },
      {
        title: "Learning Path",
        content: [
          "Take advanced math courses in high school if possible",
          "Try beginner-friendly courses on Kaggle or DataCamp",
          "Find datasets about things you're interested in and analyze them",
          "Participate in data competitions for beginners"
        ]
      },
      {
        title: "Project Ideas",
        content: [
          "Track and analyze your own habits or activities",
          "Create visualizations of interesting public data",
          "Build a recommendation system for movies or music",
          "Predict outcomes in sports or games you enjoy"
        ]
      }
    ],
    "ux-designer": [
      {
        title: "Basic Skills to Develop",
        content: [
          "Practice sketching and drawing user interfaces",
          "Learn about how people interact with apps and websites",
          "Study why some apps are easy to use and others aren't",
          "Start noticing design elements in apps you use daily"
        ]
      },
      {
        title: "Learning Path",
        content: [
          "Take art or design classes if available at your school",
          "Learn the basics of graphic design through YouTube tutorials",
          "Practice redesigning apps or websites you think could be better",
          "Create digital designs using free tools like Figma or Canva"
        ]
      },
      {
        title: "Building Experience",
        content: [
          "Design posters or graphics for school events",
          "Offer to help design websites for clubs or local businesses",
          "Create a design portfolio of your work",
          "Get feedback from friends on your designs"
        ]
      }
    ],
    "environmental-scientist": [
      {
        title: "Explore the Field",
        content: [
          "Join environmental clubs or organizations at school",
          "Volunteer for local conservation projects",
          "Start learning about ecosystems in your area",
          "Participate in citizen science projects"
        ]
      },
      {
        title: "Learning Path",
        content: [
          "Focus on sciences (biology, chemistry, earth science) in school",
          "Take part in science fairs with environmental projects",
          "Attend summer camps or programs focused on nature and conservation",
          "Read books and watch documentaries about environmental issues"
        ]
      },
      {
        title: "Getting Experience",
        content: [
          "Start a recycling or sustainability initiative at your school",
          "Keep a nature journal about local wildlife or plants",
          "Volunteer at nature centers, parks, or wildlife rehabilitation centers",
          "Join projects that monitor local water quality or pollution levels"
        ]
      }
    ],
    "marketing-specialist": [
      {
        title: "Start Building Skills",
        content: [
          "Practice creating content for social media",
          "Learn about what makes people interested in products or services",
          "Try designing simple advertisements for imaginary products",
          "Study successful marketing campaigns for brands you like"
        ]
      },
      {
        title: "Learning Path",
        content: [
          "Take business or communications classes if available",
          "Learn basic graphic design and content creation",
          "Study how people make decisions and what influences them",
          "Practice writing persuasive content"
        ]
      },
      {
        title: "Gain Experience",
        content: [
          "Help promote school events or club activities",
          "Create social media content for a school organization",
          "Offer to help local small businesses with their online presence",
          "Start a blog or YouTube channel about something you're passionate about"
        ]
      }
    ],
    "healthcare-administrator": [
      {
        title: "Exploring Healthcare",
        content: [
          "Learn about different healthcare careers and settings",
          "Volunteer at hospitals or healthcare facilities if possible",
          "Join health-related clubs at school",
          "Research how healthcare organizations work"
        ]
      },
      {
        title: "Skills to Develop",
        content: [
          "Practice organizational and planning skills",
          "Learn about healthcare systems and policies",
          "Develop strong communication skills",
          "Take health science or business classes if available"
        ]
      },
      {
        title: "Next Steps",
        content: [
          "Shadow healthcare professionals if possible",
          "Volunteer for health-focused community service",
          "Consider healthcare administration programs for college",
          "Learn about current healthcare issues and innovations"
        ]
      }
    ]
  };
  
  return guidesByCareer[careerId] || [];
};

const YouTubeEmbed: React.FC<{ videoId: string }> = ({ videoId }) => {
  return (
    <div className="aspect-video w-full overflow-hidden rounded-md">
      <iframe
        width="100%"
        height="100%"
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="aspect-video"
      ></iframe>
    </div>
  );
};

const CareerGuideDialog: React.FC<CareerGuideDialogProps> = ({
  career,
  open,
  onOpenChange,
}) => {
  const videos = getCareerVideos(career.id);
  const guides = getCareerGuides(career.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{career.title}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Teen-friendly guides and recommended videos to help you explore this career
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="guides" className="w-full mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="guides" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              Quick Guides
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-2">
              <Youtube className="h-4 w-4" />
              Cool Videos
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="guides" className="mt-4">
            <div className="space-y-4">
              {guides.length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {guides.map((guide, index) => (
                    <AccordionItem key={index} value={`guide-${index}`}>
                      <AccordionTrigger className="text-lg font-medium">
                        {guide.title}
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="list-disc pl-6 space-y-2">
                          {guide.content.map((item, itemIndex) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    Career guides for {career.title} are coming soon!
                  </p>
                </div>
              )}
              
              <div className="border-t pt-4">
                <h3 className="font-medium mb-2">Skills You'll Need</h3>
                <div className="flex flex-wrap gap-2">
                  {career.skills.map((skill) => (
                    <Badge key={skill} variant="secondary">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button variant="outline" size="sm" asChild>
                  <a 
                    href={`https://www.google.com/search?q=${encodeURIComponent(career.title)}+career+for+teens+guide`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Find more resources online
                  </a>
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="videos" className="mt-4">
            {videos.length > 0 ? (
              <div className="space-y-6">
                {videos.map((video) => (
                  <Card key={video.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="mb-4">
                        <YouTubeEmbed videoId={video.id} />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-semibold">{video.title}</h3>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>{video.channel}</span>
                          <span>{video.views} views</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <div className="text-center pt-4">
                  <Button asChild>
                    <a 
                      href={`https://www.youtube.com/results?search_query=${encodeURIComponent(career.title)}+career+guide+for+teens`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <Youtube className="h-4 w-4" />
                      See More on YouTube
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  Video recommendations for {career.title} are coming soon!
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  asChild
                >
                  <a 
                    href={`https://www.youtube.com/results?search_query=${encodeURIComponent(career.title)}+career+guide+for+teens`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Search on YouTube
                  </a>
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CareerGuideDialog;
